import { defineStore } from "pinia";
import api from "@/modules/api";
import { useMoment } from "@/stores/moment";
import { useExplore } from "@/stores/explore";
import { ContentRatingEnum, MateSearchSorterEnum, SorterEnum } from "@/enums";
import { isMobile } from "@/modules/common";
import { MateSearchableFields } from "@/enums/mateEnums";

interface State {
  tabIdx: number;
  mateKeyList: {
    data: string[];
    init: boolean;
    scrollTop: number;
    total: number | string;
    page: number;
  };
  momentKeyList: {
    data: string[];
    init: boolean;
    scrollTop: number;
    total: number | string;
    page: number;
  };
  mateMap: { [uuid: string]: MatePopular };
  emojiList: Emoji[];
  momentSort: SorterEnum;
  mateSort: MateSearchSorterEnum;
  isAll: 0 | 1;
  isNSfw: undefined | ContentRatingEnum;
}

export const PER_PAGE = isMobile() ? 16 : 32;
export const useSearch = defineStore("search", {
  state: (): State => {
    return {
      tabIdx: 0,
      mateKeyList: {
        data: [],
        init: false,
        scrollTop: 0,
        total: 0,
        page: 1,
      },
      momentKeyList: {
        data: [],
        init: false,
        scrollTop: 0,
        total: 0,
        page: 1,
      },
      mateMap: {},
      emojiList: [],
      momentSort: SorterEnum.HOT,
      mateSort: MateSearchSorterEnum.HOT,
      isAll: 0,
      isNSfw: undefined,
    };
  },
  getters: {
    mateList: (state): MatePopular[] => {
      return state.mateKeyList.data
        .map((uuid) => state.mateMap[uuid])
        .filter((item) => !!item);
    },
    momentList: (state): Moment[] => {
      const momentStore = useMoment();
      return state.momentKeyList.data
        .map((uuid) => momentStore.momentMap[uuid])
        .filter((item) => !!item)
        .filter((item) => item.parentId === null);
    },
  },
  actions: {
    async searchMateAndMoment(keyword: string) {
      await this.initMateListIfEmpty(keyword);
      await this.initMomentListIfEmpty(keyword);
    },

    async initMateListIfEmpty(keyword?: string) {
      this.mateKeyList.page = 1;
      this.mateKeyList.data = [];
      this.mateMap = {};

      const mates = await this.fetchMateList(keyword);

      this.mateKeyList.data = mates.map((item) => item.uuid);
      this.mateKeyList.init = true;
    },

    async initMomentListIfEmpty(keyword: string) {
      this.momentKeyList.page = 1;
      this.momentKeyList.data = [];

      const moments = await this.fetchMomentList(keyword);
      this.momentKeyList.data = moments.map((item) => item.uuid);
      this.momentKeyList.init = true;
    },

    async fetchMateList(
      keyword: string = "",
      page?: number,
      pageSize = PER_PAGE
    ): Promise<MatePopular[]> {
      const searchFields = [
        MateSearchableFields.TAG,
        MateSearchableFields.BIO_ENGLISH,
        MateSearchableFields.BIO_CHINESE,
        MateSearchableFields.NAME_CHINESE,
        MateSearchableFields.NAME_ENGLISH,
        MateSearchableFields.NAME,
        MateSearchableFields.BIO,
      ];
      const res = await api.readMateSearchPage({
        pagination: { perPage: pageSize, page: page },
        filter: {
          query: keyword,
          searchFields,
          isNsfw: this.isNSfw,
          isProfileSet: this.isAll ? undefined : 1,
        },
        tab: this.mateSort,
      });
      const data = res.page.data;
      this.mateKeyList.total = res.page.total
        ? res.page.total > 99
          ? "99+"
          : res.page.total
        : 0;
      const explore = useExplore();
      explore.searchTotalCount = res.page.total!;
      this.updateMateMap(data);
      return data;
    },

    updateMomentMap(moments: Moment[]) {
      const moment = useMoment();
      moment.updateMomentMap(moments);
    },

    updateMateMap(mates: MatePopular[]) {
      mates.forEach((mate) => {
        this.mateMap[mate.uuid] = mate;
      });
    },

    async fetchMomentList(
      keyword: string,
      page?: number,
      pageSize = PER_PAGE
    ): Promise<Moment[]> {
      const res = await api.readMomentSearchPage({
        pagination: { perPage: pageSize, page: page },
        filter: { query: keyword },
        tab: this.momentSort,
      });
      const data = res.page.data;
      this.momentKeyList.total = res.page.total
        ? res.page.total > 99
          ? "99+"
          : res.page.total
        : 0;
      const explore = useExplore();
      explore.searchTotalCount = res.page.total!;
      this.updateMomentMap(data);
      return data;
    },

    async fetchMoreMateList(keyword: string): Promise<boolean> {
      const currentList = this.mateKeyList.data;
      this.mateKeyList.page++;
      const mates = await this.fetchMateList(keyword, this.mateKeyList.page);
      if (mates.length === 0) {
        return false;
      }
      currentList.push(...mates.map((item) => item.uuid));
      return true;
    },

    async fetchMoreMomentList(keyword: string): Promise<boolean> {
      const currentList = this.momentKeyList.data;
      this.momentKeyList.page++;
      const mates = await this.fetchMomentList(
        keyword,
        this.momentKeyList.page
      );
      if (mates.length === 0) {
        return false;
      }
      currentList.push(...mates.map((item) => item.uuid));
      return true;
    },
  },
});
